import type { JSX } from 'react';

/**
 * Реестр рисованных SVG-иконок бейджей (редизайн наград · Genie 25 JUN: уход от
 * эмодзи к ink/riso-глифам). Каталог (`BadgeDefinition.icon`) хранит строковый ключ,
 * здесь — сама графика. Пока ключ не задан / нет в реестре → стена и тост рендерят
 * эмодзи (постепенная миграция по блокам). Стиль: единый штрих `--ink`, акцентная
 * заливка по смыслу, viewBox 0 0 24 24. Мокап: 04_DESIGN/mockup_badge_icons_drawn.html.
 *
 * Волна 1 (25 JUN): 7 иконок под 9 экзамен-бейджей. Дальше — B1→B4.
 */
const ICONS: Record<string, JSX.Element> = {
  // ── Экзамены-вехи ──────────────────────────────────────────────────────
  'exam-peak-flag': (
    <>
      <path d="M2 20 L8.5 8 L12.5 14 L15 10 L22 20 Z" fill="var(--teal)" />
      <path d="M8.5 8 V3.2" />
      <path d="M8.5 3.4 H13 l-1.4 1.5 L13 6.4 H8.5" fill="var(--accent)" />
    </>
  ),
  'exam-signpost': (
    <>
      <path d="M12 21 V5" />
      <path d="M12 6 H19.5 L21.5 8.4 L19.5 10.8 H12 Z" fill="var(--teal)" />
      <path d="M7.5 21 H16.5" />
    </>
  ),
  'exam-summit': (
    <>
      <path d="M1.5 20 L7 10 L10.5 15 L14 6 L18 13 L22.5 20 Z" fill="var(--teal)" strokeWidth={1.6} />
      <path d="M14 6 L12.4 9 H15.6 Z" fill="var(--surface)" stroke="none" />
    </>
  ),
  'exam-ace-star': (
    <path
      d="M12 3 l2.5 5.6 6.1 .5 -4.6 4 1.4 6 -5.4 -3.2 -5.4 3.2 1.4 -6 -4.6 -4 6.1 -.5 Z"
      fill="var(--mustard)"
      strokeWidth={1.5}
    />
  ),
  'exam-laurel': (
    <>
      <circle cx="12" cy="9.5" r="5.5" fill="var(--accent)" />
      <path d="M9.6 9.4 l1.7 1.7 3.1 -3.4" stroke="var(--surface)" strokeWidth={1.8} />
      <path d="M8.4 14 L6.5 21 L12 18 L17.5 21 L15.6 14" fill="var(--accent-soft)" />
    </>
  ),
  'exam-bullseye': (
    <>
      <circle cx="12" cy="12" r="8.2" fill="var(--surface)" strokeWidth={1.6} />
      <circle cx="12" cy="12" r="4.6" fill="var(--accent-soft)" strokeWidth={1.6} />
      <circle cx="12" cy="12" r="1.6" fill="var(--accent-deep)" stroke="none" />
      <path d="M3.5 20.5 L10 14" strokeWidth={1.6} />
      <path d="M2.6 21.4 l1.2 -3 1.8 1.8 Z" fill="var(--ink)" stroke="none" />
    </>
  ),
  'exam-comeback': (
    <>
      <path d="M3 9 C6 19, 10 18, 12.5 12 S 17 5 20.5 7" fill="none" stroke="var(--accent)" strokeWidth={1.7} />
      <path d="M20.8 7 l-3.4 -.6 1.2 3.2 Z" fill="var(--accent)" stroke="none" />
      <circle cx="4.5" cy="6.5" r="1.5" fill="var(--ink-mute)" stroke="none" />
    </>
  ),

  // ── Блок 1 · волна 2 (25 JUN) ───────────────────────────────────────────
  // Спринт-кор'ы — топик-глифы; ранги/ательер — общий глиф на семейство.
  'core-mindset': (
    <>
      <circle cx="12" cy="9.5" r="5.5" fill="var(--mustard-soft)" />
      <path d="M9.5 15 H14.5 M10 18 H14" />
      <path d="M12 6.5 V10 M10.3 11 H13.7" />
    </>
  ),
  'core-requirements': (
    <>
      <path d="M6 3 H14 L18 7 V21 H6 Z" fill="var(--teal-soft)" />
      <path d="M14 3 V7 H18" />
      <path d="M8.5 12 l1.6 1.6 3.4 -3.6" strokeWidth={1.8} />
      <path d="M8.5 16.5 H14.5" />
    </>
  ),
  'core-test-types': (
    <>
      <path d="M12 3 L21 7.5 L12 12 L3 7.5 Z" fill="var(--teal-soft)" />
      <path d="M3.5 12 L12 16 L20.5 12" />
      <path d="M3.5 16 L12 20 L20.5 16" />
    </>
  ),
  'core-docs': (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" fill="var(--surface)" />
      <rect x="9" y="2.5" width="6" height="3.6" rx="1.2" fill="var(--teal)" />
      <path d="M8 11 H16 M8 14.5 H16 M8 18 H13" stroke="var(--ink-soft)" />
    </>
  ),
  'core-test-design': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" fill="var(--surface)" />
      <path d="M4 9.3 H20 M4 14.6 H20 M9.3 4 V20 M14.6 4 V20" stroke="var(--ink-soft)" />
      <rect x="14.6" y="14.6" width="5.4" height="5.4" fill="var(--accent-soft)" stroke="none" />
    </>
  ),
  'ctfl-cap': (
    <>
      <path d="M2 9 L12 5 L22 9 L12 13 Z" fill="var(--teal)" />
      <path d="M6.5 11 V15 C6.5 15, 9 17, 12 17 S17.5 15 17.5 15 V11" />
      <path d="M22 9 V13.5" />
    </>
  ),
  'rank-trainee': (
    <>
      <path d="M12 3 L19 5.5 V11 C19 16, 15 19, 12 21 C9 19, 5 16, 5 11 V5.5 Z" fill="var(--teal-soft)" />
      <path
        d="M12 8 l1.2 2.6 2.8 .2 -2.1 1.9 .6 2.8 -2.5 -1.5 -2.5 1.5 .6 -2.8 -2.1 -1.9 2.8 -.2 Z"
        fill="var(--teal)"
        stroke="none"
      />
    </>
  ),
  'atelier-resident': (
    <>
      <path
        d="M12 4 C6.5 4 3 7.5 3 11.5 C3 14 5 15 7 15 C8.5 15 9 16.5 9 18 C9 19.5 10.5 20 12 20 C17.5 20 21 16 21 11.5 C21 7 17 4 12 4 Z"
        fill="var(--mustard-soft)"
      />
      <circle cx="8" cy="10" r="1.2" fill="var(--accent)" stroke="none" />
      <circle cx="12" cy="8.2" r="1.2" fill="var(--teal)" stroke="none" />
      <circle cx="16" cy="10" r="1.2" fill="var(--mustard-deep)" stroke="none" />
    </>
  ),
  'rank-detective': (
    <>
      <circle cx="10.5" cy="10.5" r="5.5" fill="var(--accent-soft)" />
      <path d="M14.6 14.6 L20 20" strokeWidth={2.2} />
    </>
  ),
  'rank-author': (
    <>
      <path
        d="M4 20 C8 18, 9 14, 13 9 C16 5.5, 19 4, 20 4 C20 5, 18.5 8, 15 11 C10.5 15, 6.5 16, 4 20 Z"
        fill="var(--teal-soft)"
      />
      <path d="M5 19 L9 15" />
    </>
  ),
  'rank-ctfl': (
    <>
      <path d="M9 13.5 L7.5 21 L12 18.5 L16.5 21 L15 13.5" fill="var(--mustard-soft)" />
      <circle cx="12" cy="9" r="5.5" fill="var(--mustard)" />
      <path d="M9.6 9 l1.6 1.6 3.2 -3.4" stroke="var(--surface)" strokeWidth={1.8} />
    </>
  ),
  owl: (
    <>
      <path
        d="M5 9 C5 5, 9 4, 12 4 C15 4, 19 5, 19 9 C19 15, 16 19.5, 12 19.5 C8 19.5, 5 15, 5 9 Z"
        fill="var(--teal-soft)"
      />
      <circle cx="9" cy="10" r="2.2" fill="var(--surface)" />
      <circle cx="15" cy="10" r="2.2" fill="var(--surface)" />
      <circle cx="9" cy="10" r="0.9" fill="var(--ink)" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="var(--ink)" stroke="none" />
      <path d="M12 12.5 l-1.3 1.6 H13.3 Z" fill="var(--mustard)" stroke="none" />
      <path d="M5.5 6 L7.8 8 M18.5 6 L16.2 8" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4 H16 V9 C16 12, 14.2 13.5, 12 13.5 S8 12, 8 9 Z" fill="var(--mustard)" />
      <path d="M8 5.5 H5.2 C5.2 9, 7 10, 8.6 10 M16 5.5 H18.8 C18.8 9, 17 10, 15.4 10" />
      <path d="M12 13.5 V17 M9.5 20 H14.5 L14 17 H10 Z" fill="var(--mustard-soft)" />
    </>
  ),
  glossary: (
    <>
      <path
        d="M12 6 C9.5 4.3, 5.5 4.3, 3 5.3 V18.3 C5.5 17.3, 9.5 17.3, 12 19 C14.5 17.3, 18.5 17.3, 21 18.3 V5.3 C18.5 4.3, 14.5 4.3, 12 6 Z"
        fill="var(--teal-soft)"
      />
      <path d="M12 6 V19" />
    </>
  ),
  'first-check': (
    <>
      <circle cx="11" cy="12.5" r="6.5" fill="var(--accent-soft)" />
      <path d="M7.8 12.5 l2.4 2.5 4.6 -5" strokeWidth={2} />
      <path d="M19 3.5 l.7 2 2 .7 -2 .7 -.7 2 -.7 -2 -2 -.7 2 -.7 Z" fill="var(--mustard)" stroke="none" />
    </>
  ),

  // ── Блоки 2-4 · офсет-riso (icon_handoff_spec §B · 11 JUL) ────────────────
  // Силуэт-заливка со сдвигом (stroke="none", чтоб обводка обёртки не удваивала
  // контур) + ink-оверлей `<g stroke=var(--ink) 1.6>`. Hex спеки → палитра-vars.
  release: (
    <>
      <path d="M12 2.8 C15 4.8 16.2 8.8 14.6 13.8 H9.4 C7.8 8.8 9 4.8 12 2.8 Z" fill="var(--mustard)" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M12 2.8 C15 4.8 16.2 8.8 14.6 13.8 H9.4 C7.8 8.8 9 4.8 12 2.8 Z" />
        <circle cx="12" cy="8.4" r="1.7" fill="var(--surface)" />
        <path d="M9.4 12 L6.8 14.8 L9 15.6 M14.6 12 L17.2 14.8 L15 15.6" />
        <path d="M10.6 16.5 C10.6 18.5 11 20 12 21.4 C13 20 13.4 18.5 13.4 16.5" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'api-master': (
    <>
      <path d="M7.5 7.5 H16.5 V11 C16.5 13.8 14.5 15.6 12 15.6 S7.5 13.8 7.5 11 Z" fill="var(--teal)" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.7}>
        <path d="M9 7.5 V3.5 M15 7.5 V3.5" />
        <path d="M7.5 7.5 H16.5 V11 C16.5 13.8 14.5 15.6 12 15.6 S7.5 13.8 7.5 11 Z" />
        <path d="M12 15.6 V18.6 C12 20.2 13.1 21 14.8 21" />
      </g>
    </>
  ),
  'security-watcher': (
    <>
      <path d="M12 3 L19 5.5 V11 C19 16 15 19 12 21 C9 19 5 16 5 11 V5.5 Z" fill="var(--green)" opacity="0.55" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M12 3 L19 5.5 V11 C19 16 15 19 12 21 C9 19 5 16 5 11 V5.5 Z" />
        <path d="M9 11.6 l2 2 4 -4.3" strokeWidth={1.9} />
      </g>
    </>
  ),
  'ai-qa-master': (
    <>
      <path d="M12 5.7 C9.7 5.7 8.3 7 8.3 8.4 C6.5 8.7 5.5 10 5.5 11.6 C5.5 13.2 6.4 14.5 7.9 15 C7.9 16.7 9.5 18 11.5 18 H12.5 C14.5 18 16.1 16.7 16.1 15 C17.6 14.5 18.5 13.2 18.5 11.6 C18.5 10 17.5 8.7 15.7 8.4 C15.7 7 14.3 5.7 12 5.7 Z" fill="var(--accent)" opacity="0.8" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M12 5.7 C9.7 5.7 8.3 7 8.3 8.4 C6.5 8.7 5.5 10 5.5 11.6 C5.5 13.2 6.4 14.5 7.9 15 C7.9 16.7 9.5 18 11.5 18 H12.5 C14.5 18 16.1 16.7 16.1 15 C17.6 14.5 18.5 13.2 18.5 11.6 C18.5 10 17.5 8.7 15.7 8.4 C15.7 7 14.3 5.7 12 5.7 Z" />
        <path d="M12 5.7 V18" />
        <path d="M19.4 3 l.6 1.7 1.7 .6 -1.7 .6 -.6 1.7 -.6 -1.7 -1.7 -.6 1.7 -.6 Z" fill="var(--mustard)" stroke="none" />
      </g>
    </>
  ),
  'early-bird': (
    <>
      <path d="M7.6 16.8 A4.6 4.6 0 0 1 16.4 16.8 Z" fill="var(--mustard)" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M7.4 17 A4.7 4.7 0 0 1 16.6 17" />
        <path d="M4 17 H20" />
        <path d="M12 9.5 V6.8 M6.6 11.6 L4.8 9.8 M17.4 11.6 L19.2 9.8" />
        <path d="M8.5 20 H15.5" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'sql-detective': (
    <>
      <circle cx="10.5" cy="10.5" r="5.8" fill="var(--teal)" opacity="0.5" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <circle cx="10.5" cy="10.5" r="5.8" />
        <path d="M14.9 14.9 L20 20" strokeWidth={2} />
        <ellipse cx="10.5" cy="8.3" rx="3.4" ry="1.4" />
        <path d="M7.1 8.3 V12.6 C7.1 13.4 8.6 14.1 10.5 14.1 S13.9 13.4 13.9 12.6 V8.3" />
      </g>
    </>
  ),
  'ci-wrangler': (
    <>
      <circle cx="12" cy="12" r="4.4" fill="var(--green)" opacity="0.6" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 4 V6.4 M12 17.6 V20 M4 12 H6.4 M17.6 12 H20 M6.4 6.4 L8.1 8.1 M15.9 15.9 L17.6 17.6 M17.6 6.4 L15.9 8.1 M8.1 15.9 L6.4 17.6" />
      </g>
    </>
  ),
  'curious-cat': (
    <>
      <path d="M5.5 10.5 V5 L9.3 7.8 H14.7 L18.5 5 V10.5 C18.5 14.8 15.6 17.4 12 17.4 S5.5 14.8 5.5 10.5 Z" fill="var(--accent)" opacity="0.75" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M5.5 10.5 V5 L9.3 7.8 H14.7 L18.5 5 V10.5 C18.5 14.8 15.6 17.4 12 17.4 S5.5 14.8 5.5 10.5 Z" />
        <circle cx="9.6" cy="11" r="0.9" fill="var(--ink)" stroke="none" />
        <circle cx="14.4" cy="11" r="0.9" fill="var(--ink)" stroke="none" />
        <path d="M11 14 C11.6 14.5 12.4 14.5 13 14" />
        <path d="M4 12.8 L1.8 12.4 M4.2 14.6 L2.2 15.4 M20 12.8 L22.2 12.4 M19.8 14.6 L21.8 15.4" />
        <path d="M12 17.4 V20.6 C12 21.4 12.8 21.8 13.6 21.4" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'bug-hunter': (
    <>
      <ellipse cx="12" cy="13" rx="3.4" ry="4.2" fill="var(--accent)" opacity="0.8" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <circle cx="12" cy="12.6" r="8" />
        <path d="M12 2.4 V5.4 M12 19.8 V22.8 M2.4 12.6 H5.4 M18.6 12.6 H21.6" stroke="var(--accent-deep)" />
        <ellipse cx="12" cy="13" rx="3.4" ry="4.2" />
        <circle cx="12" cy="7.9" r="1.7" />
        <path d="M8.6 11.4 L6.6 10.4 M8.6 14.6 L6.6 15.6 M15.4 11.4 L17.4 10.4 M15.4 14.6 L17.4 15.6" />
      </g>
    </>
  ),
  'first-api-request': (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" fill="var(--teal)" opacity="0.55" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M7 9.5 L9.8 12 L7 14.5" />
        <path d="M11.5 15 H14" stroke="var(--accent-deep)" />
        <path d="M14.8 8.2 L18.4 8.2 M16.6 6.4 L18.4 8.2 L16.6 10" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'shipped-v2': (
    <>
      <path d="M5 9 L12 5.5 L19 9 V17 L12 20.5 L5 17 Z" fill="var(--mustard)" opacity="0.7" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M5 9 L12 5.5 L19 9 V17 L12 20.5 L5 17 Z" />
        <path d="M5 9 L12 12.5 L19 9 M12 12.5 V20.5" />
        <path d="M12 5.5 V2.2 M12 2.2 H15.4 l-1.1 1.2 1.1 1.2 H12" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'hacker-mindset': (
    <>
      <path d="M12 4 C7.8 4 5.4 6.8 5.4 10.2 C5.4 12.6 6.8 14.1 8.2 15 V17.8 H15.8 V15 C17.2 14.1 18.6 12.6 18.6 10.2 C18.6 6.8 16.2 4 12 4 Z" fill="var(--mustard)" opacity="0.75" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M12 4 C7.8 4 5.4 6.8 5.4 10.2 C5.4 12.6 6.8 14.1 8.2 15 V17.8 H15.8 V15 C17.2 14.1 18.6 12.6 18.6 10.2 C18.6 6.8 16.2 4 12 4 Z" />
        <circle cx="9.4" cy="10.5" r="1.3" fill="var(--ink)" stroke="none" />
        <circle cx="14.6" cy="10.5" r="1.3" fill="var(--ink)" stroke="none" />
        <path d="M10 17.8 V20.5 M14 17.8 V20.5 M12 17.8 V21" strokeWidth={1.4} />
      </g>
    </>
  ),
  'polyglot-block-2': (
    <>
      <circle cx="11" cy="9.5" r="5.4" fill="var(--green)" opacity="0.6" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <circle cx="11" cy="9.5" r="5.4" />
        <path d="M16 8 L20.4 9.5 L16 11.2" />
        <circle cx="9.6" cy="8.2" r="0.9" fill="var(--ink)" stroke="none" />
        <path d="M11 14.9 C11 17.4 10 19.5 8 21" />
        <path d="M7.2 12.6 C8.8 11.4 10.4 11.4 12 12.6" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  polyglot: (
    <>
      <g fill="var(--mustard)" opacity="0.7" stroke="none" transform="translate(1.1,0.9)">
        <rect x="4" y="14.5" width="13" height="4" rx="1" />
        <rect x="5.5" y="10" width="10" height="4" rx="1" />
      </g>
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <rect x="4" y="14.5" width="13" height="4" rx="1" />
        <rect x="5.5" y="10" width="10" height="4" rx="1" />
        <rect x="7" y="5.5" width="7" height="4" rx="1" />
        <path d="M17 4.5 H21.5 V8 H19 L17.6 9.4 V8 H17 Z" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'selenium-recorder': (
    <>
      <g fill="var(--teal)" opacity="0.6" stroke="none" transform="translate(1.1,0.9)">
        <rect x="4" y="10" width="16" height="9" rx="1.5" />
        <path d="M4 10 L5.5 5.5 L20 7 L19.5 10 Z" />
      </g>
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <rect x="4" y="10" width="16" height="9" rx="1.5" />
        <path d="M4 10 L5.5 5.5 L20 7 L19.5 10 Z" />
        <path d="M8.3 5.8 L10 9 M12.6 6.2 L14.3 9.4 M16.9 6.7 L18.4 9.7" />
        <circle cx="12" cy="14.5" r="1.6" fill="var(--accent-deep)" stroke="var(--accent-deep)" />
      </g>
    </>
  ),
  'flake-slayer': (
    <>
      <ellipse cx="12" cy="12.5" rx="4" ry="5" fill="var(--green)" opacity="0.6" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <ellipse cx="12" cy="12.5" rx="4" ry="5" />
        <circle cx="12" cy="6.2" r="2" />
        <path d="M8.4 10.5 L5.8 9.3 M8.4 14.5 L5.8 15.7 M15.6 10.5 L18.2 9.3 M15.6 14.5 L18.2 15.7" />
        <path d="M3.5 21 L20.5 3.5" stroke="var(--accent-deep)" strokeWidth={2.2} />
      </g>
    </>
  ),
  'coverage-champion': (
    <>
      <g fill="var(--teal)" opacity="0.6" stroke="none" transform="translate(1.1,0.9)">
        <rect x="5" y="11" width="3.4" height="8" />
        <rect x="10.3" y="6" width="3.4" height="13" />
        <rect x="15.6" y="9" width="3.4" height="10" />
      </g>
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <rect x="5" y="11" width="3.4" height="8" />
        <rect x="10.3" y="6" width="3.4" height="13" />
        <rect x="15.6" y="9" width="3.4" height="10" />
        <path d="M3.5 19 H20.5" />
        <path d="M15.5 3.2 l.5 1.4 1.4 .5 -1.4 .5 -.5 1.4 -.5 -1.4 -1.4 -.5 1.4 -.5 Z" fill="var(--accent-deep)" stroke="none" />
      </g>
    </>
  ),
  'prompt-injector': (
    <>
      <path d="M8.3 19.7 L4.3 15.7 L12.5 7.5 L16.5 11.5 Z" fill="var(--accent)" opacity="0.75" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M8.3 19.7 L4.3 15.7 L12.5 7.5 L16.5 11.5 Z" />
        <path d="M14.5 5.5 L18.5 9.5 M13 7 L17 3 M21 7 L17 3 M15.5 8.5 L18 6" />
        <path d="M4.3 15.7 L2.2 21.8 L8.3 19.7" stroke="var(--accent-deep)" />
        <path d="M8.6 14.2 l2 2 M10.6 12.2 l2 2" stroke="var(--accent-deep)" strokeWidth={1.2} />
      </g>
    </>
  ),
  'bias-detector': (
    <>
      <path d="M7 4.5 C10 6 14 6 17 4.5 C17.5 11 15.5 16.5 12 16.5 C8.5 16.5 6.5 11 7 4.5 Z" fill="var(--mustard)" opacity="0.7" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <path d="M7 4.5 C10 6 14 6 17 4.5 C17.5 11 15.5 16.5 12 16.5 C8.5 16.5 6.5 11 7 4.5 Z" />
        <path d="M9.3 9 C9.8 8.4 10.6 8.4 11.1 9" />
        <path d="M12.9 9.6 C13.4 10.2 14.2 10.2 14.7 9.6" stroke="var(--accent-deep)" />
        <path d="M10 13 C11.3 12.2 13 12.6 14 13.6" stroke="var(--accent-deep)" />
        <path d="M12 16.5 V19 M9 20.5 C10.5 19.5 13.5 19.5 15 20.5" />
      </g>
    </>
  ),
  'career-launch': (
    <>
      <circle cx="12" cy="14.5" r="4.6" fill="var(--accent)" opacity="0.8" stroke="none" transform="translate(1.1,0.9)" />
      <g fill="none" stroke="var(--ink)" strokeWidth={1.6}>
        <circle cx="12" cy="14.5" r="4.6" />
        <path d="M9.6 10.6 L7 3.5 H11 L12 6 L13 3.5 H17 L14.4 10.6" />
        <path d="M12 12.6 l.7 1.5 1.6 .2 -1.2 1.1 .3 1.6 -1.4 -.8 -1.4 .8 .3 -1.6 -1.2 -1.1 1.6 -.2 Z" fill="var(--accent-deep)" stroke="none" />
      </g>
    </>
  ),
};

/** Есть ли в реестре рисованная иконка под этот ключ. */
export function hasBadgeIcon(iconKey?: string): boolean {
  return !!iconKey && iconKey in ICONS;
}

/**
 * Рисованная иконка бейджа по ключу. Возвращает null, если ключа нет в реестре —
 * вызывающий код сам решает фолбэк на эмодзи (см. Profile / BadgeToast).
 */
export function BadgeIcon({ iconKey, size = 38 }: { iconKey: string; size?: number }) {
  const body = ICONS[iconKey];
  if (!body) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="var(--ink)"
      strokeWidth={1.7}
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {body}
    </svg>
  );
}
